import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { db } from '../firebase';
import {
    query,
    collection,
    onSnapshot,
    orderBy,
    where,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';
import { UserAuth } from '../context/AuthContext';
import { BarLoader } from 'react-spinners';
import { Menu, Transition, Listbox, Dialog } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import StoryDetailEdit from "../components/StoryDetailEdit";
//import UserPermissions from "../components/UserPermissions";
import Skeleton from 'react-loading-skeleton';
import { SkeletonTheme } from 'react-loading-skeleton';
import KanbanBoard from './KanbanBoard';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PathProductDiscovery from './PathProductDiscovery';
import DiscoveryTile from './DiscoveryTile';
import { discoveryTilesData } from '../data/discoveryTilesData';
import { logCustomEvent } from './UtilAnalytics';
import ReactDOM from 'react-dom';

const PathLibrary = ({ isHome = false }) => {
    const { user } = UserAuth();
    const navigate = useNavigate();
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [storiesPerPage, setStoriesPerPage] = useState(isHome ? 5 : 6);
    const [visibilityFilter, setVisibilityFilter] = useState('all');
    const [sharingFilter, setSharingFilter] = useState('all');
    const [isKanbanModalOpen, setIsKanbanModalOpen] = useState(false);
    const [selectedStoryData, setSelectedStoryData] = useState(null);
    const [activeTabStart, setActiveTabStart] = useState('discovery');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [storyToDelete, setStoryToDelete] = useState(null);


    const baseColor = '#18181b';
    const highlightColor = '#27272a';

    const headerContent = isHome ? (
        <div className="flex justify-between items-center pb-4">
            <div className="block tracking-normal text-lg leading-relaxed text-zinc-200 inline-flex items-center">
                <img src="/util/app/b3-admin-theater-3.svg" className="h-10 inline-block mr-3 opacity-75 rounded-full p-1" />
                Recent Paths
            </div>
            <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <Link to="/app/storyboard?type=blank" className="px-4 py-2 text-sm bg-violet-950 hover:bg-violet-900 text-sky-400 rounded-md transition-colors">
                    New Path
                </Link>
            </motion.div>
        </div>
    ) : (
        <div className="flex justify-between items-center ">
            <h2 className="text-2xl font-bold text-zinc-200">Path Library</h2>
            <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <Link to="/app/storyboard?type=blank" className="px-4 py-2 text-sm bg-violet-950 hover:bg-violet-900 text-sky-400 rounded-md transition-colors">
                    New Path
                </Link>
            </motion.div>
        </div>
    );

    // Fetch stories data
    useEffect(() => {
        if (user?.uid) {
            const q = query(
                collection(db, 'stories'),
                orderBy('lastModified', 'desc'),
                where("role.owner", "array-contains", user?.uid)
            );

            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                let storiesArr = [];
                querySnapshot.forEach((doc) => {
                    const storyData = { ...doc.data(), id: doc.id };
                    // Only include stories that are of type "tell"
                    if (storyData.type === "tell" && storyData.intelligence) {
                        // Ensure the object pushed includes the unique document ID
                        if (storyData.intelligence) {
                            storiesArr.push({ ...storyData.intelligence, id: doc.id, lastModified: storyData.lastModified });
                        }
                    } else if (storyData.type === "tell" && !storyData.intelligence) {
                        //console.warn(`Story with ID ${doc.id} has type "tell" but no intelligence data.`);
                    }
                });
                // console.log('--- STORIES ARR ---')
                // console.log(storiesArr)
                // console.log('--- STORIES ARR ---')
                // console.log(storiesArr)
                // console.log('--- STORIES ARR ---')
                setStories(storiesArr);
                setLoading(false);
            });

            return () => unsubscribe();
        }
    }, [user]);

    // Effect to update storiesPerPage and currentPage based on isHome prop
    useEffect(() => {
        if (isHome) {
            setStoriesPerPage(5);
            setCurrentPage(1); // Reset to first page for home view
        } else {
            setStoriesPerPage(6); // Default for non-home view
            // Optionally reset current page if needed when switching away from home
            // setCurrentPage(1); 
        }
    }, [isHome]);

    const [storyActiveEdit, setStoryActiveEdit] = useState()
    const [storyActivePerm, setStoryActivePerm] = useState()

    const generateStoryBite = async (item) => {
        if (!selectedStoryData) {
            console.error("generateStoryBite called but selectedStoryData is null.");
            toast.error("Error: No story selected to generate bite.");
            return null;
        }

        const randomVersion = Math.floor(Math.random() * 100000);
        logCustomEvent('gen_story_bite', {})

        const apiEndpoint = 'https://us-central1-brevit3-57b00.cloudfunctions.net/';

        const payload = {
            producedWrapper: selectedStoryData.producedResult,
            story: selectedStoryData.storyChapters,
            storyData: selectedStoryData.storyChapters,
            personaData: selectedStoryData.personaData,
            researchData: selectedStoryData.researchData,
            businessNewsData: selectedStoryData.businessNewsData,
            industryTrendsData: selectedStoryData.industryTrendsData,
            formData: selectedStoryData.scenario,
            scenario: selectedStoryData.scenario,
            intelligenceFoundation: item,
            transcriptData: selectedStoryData.transcriptData,
            version: randomVersion,
            executiveInterview: selectedStoryData.researchData?.podcastInsights,
            tone: selectedStoryData.storyTone?.story,
            storyIntensity: selectedStoryData.storyTone?.storyIntensity,
            storyLength: selectedStoryData.storyTone?.storyLength,
            item: item
        };

        // Convert undefined payload values to null
        Object.keys(payload).forEach(key => {
            if (payload[key] === undefined) {
                payload[key] = null;
            }
        });

        const storyBiteResponse = await fetch(apiEndpoint + 'jobsStoryBite', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const storyBiteResult = await storyBiteResponse.json();
        //console.log('StoryBite Generation API Response:', storyBiteResult);
        //setStoryBiteData(storyBiteResponse);
        // playSound(); // playSound is not defined in this component's scope
        return storyBiteResult;
    }

    const openDeleteModal = (story) => {
        setStoryToDelete(story);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setStoryToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!storyToDelete) return;

        const storyId = storyToDelete.id;
        const storyRef = doc(db, 'stories', storyId);

        try {
            await deleteDoc(storyRef);
            toast.success('Path deleted successfully!');
            closeDeleteModal();
        } catch (error) {
            console.error("Error deleting story: ", error);
            toast.error('Failed to delete path.');
        }
    };

    const filteredStories = stories.filter(story => {
        // Search filter
        const nameMatch = (story.scenario.promptCustomerName ?? '').toLowerCase().includes(searchQuery.toLowerCase());
        const descriptionMatch = (story.scenario.promptCustomerName ?? '').toLowerCase().includes(searchQuery.toLowerCase());
        const roleMatch = (story.scenario.promptCustomerRole ?? '').toLowerCase().includes(searchQuery.toLowerCase());
        const problemMatch = (story.scenario.promptProblem ?? '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesSearch = nameMatch || descriptionMatch || roleMatch || problemMatch;

        // Visibility filter
        //const matchesVisibility = visibilityFilter === 'all' || story.visibility === visibilityFilter;

        return matchesSearch;
    });

    // Calculate pagination
    const indexOfLastStory = currentPage * storiesPerPage;
    const indexOfFirstStory = indexOfLastStory - storiesPerPage;
    const currentStories = filteredStories.slice(indexOfFirstStory, indexOfLastStory);
    const totalPages = Math.ceil(filteredStories.length / storiesPerPage);

    const handleStoryClick = (story) => {
        setSelectedStoryData(story);
        setIsKanbanModalOpen(true);
    };

    const ListViewItem = ({ story, onClick, onDeleteClick, isHome }) => {
        let formData = {};
        try {
            if (story.formData) {
                formData = JSON.parse(story.formData);
            }
        } catch (e) {
            console.error("Error parsing formData JSON:", e);
        }

        const handleDeleteButtonClick = (e) => {
            e.stopPropagation();
            onDeleteClick(story);
        };

        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                whileHover={{ scale: 1.005 }}
                className={`hover:bg-zinc-950/40 rounded-lg transition-all duration-200 items-start cursor-pointer relative group ${isHome ? 'p-4 bg-zinc-950/20  ' : 'bg-black/20 md:p-6 p-2 gap-4'}`}
                onClick={() => onClick(story)}
            >
                {!isHome && (
                    <button
                        onClick={handleDeleteButtonClick}
                        className="absolute top-4 right-4 p-1.5 bg-zinc-700/50 hover:bg-red-500/80 text-zinc-400 hover:text-white rounded-full transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2   focus:ring-red-500"
                        aria-label="Delete Path"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                )}

                <div className={`${isHome ? 'flex flex-row items-start w-full' : ''}`}>
                    {/* Left Column: Heading and Small Icons (only when isHome) */}
                    <div className={`${isHome ? 'flex flex-col flex-grow min-w-0 pr-2' : 'min-w-0 w-full col-span-5 pr-4 pl-2'}`}>
                        <h3 className={`text-zinc-50 mb-1 font-normal truncaxte transition-colors ${isHome ? 'text-lg' : 'text-xl'}`}>
                            {story.scenario?.promptCustomerName || 'Untitled Story'} - {story.scenario?.promptCompanyName || 'No Company'} - {story.scenario?.promptProductName || 'No Product'}
                        </h3>
                        {isHome && ( // Display role and problem if isHome is true
                            <>
                                <div className="text-zinc-400 text-sm mb-1 truncate">
                                    {story.scenario?.promptCustomerRole || 'No Role'} - {story.scenario?.promptCustomerProblem || 'No Problem Description'}
                                </div>
                                {/* Removed the story.maxDeepData part for isHome as it was not requested */}
                            </>
                        )}
                        {!isHome && (
                            <>
                                <div className="text-zinc-100 text-sm mb-1 truncate">
                                    {story.scenario?.promptCustomerRole || 'No Role'} - {story.scenario?.promptCustomerProblem || 'No Problem Description'}
                                </div>
                                <div className="grid grid-cols-1 col-span-4 gap-4 text-zinc-400 leading-7 mb-8 mt-4">
                                    {story.maxDeepData?.researchDeepData?.story ?
                                        story.maxDeepData.researchDeepData.story.split(' ').slice(0, 20).join(' ') + '...'
                                        : ''}
                                </div>
                            </>
                        )}
                        {/* Small Icon Buttons (only when isHome) */}
                        {/* {isHome && (
                            <div className="flex flex-row flex-wrap items-center gap-2 mt-2">
                                {discoveryTilesData
                                    .filter(tile => tile.id !== 'conversation') // Exclude 'Ask Anything'
                                    .map((tile) => (
                                        <div key={tile.id} className="relative group/tooltip">
                                            <DiscoveryTile
                                                icon={tile.icon}
                                                title={tile.title}
                                                // description is not needed for icon-only
                                                bgColor={tile.bgColor}
                                                borderColor={tile.borderColor}
                                                textColor={tile.textColor}
                                                iconBgColor={tile.iconBgColor}
                                                onClick={() => setIsKanbanModalOpen(true)}
                                                tabValue={tile.tabValue}
                                                setActiveTabStart={setActiveTabStart}
                                                style={'icons-only'}
                                            />
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-3 py-1.5 bg-zinc-950 text-white text-base rounded-md opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                                {tile.title}
                                                <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-zinc-950 rotate-45"></div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )} */}
                    </div>

                    {/* Buttons Container (handles both isHome and !isHome layouts for Ask Anything or all buttons) */}
                    <div className={`min-w-0 ${isHome ? 'flex-shrink-0' : 'w-full grid col-span-5 gap-4'}`}>
                        <div className={`${isHome ? 'flex justify-end' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 col-span-4 gap-1 -mx-1'}`}>
                            {isHome ? (
                                <div className="flex flex-col items-end"> {/* Wrapper for date and button */}
                                    {story.lastModified && (
                                        <p className="text-zinc-300 text-xs mb-0">
                                            {/* {formatDistanceToNow(story.lastModified.toDate(), { addSuffix: true })} */}
                                        </p>
                                    )}
                                    {discoveryTilesData
                                        .filter(tile => tile.id === 'conversation') // Only 'Ask Anything'
                                        .map((tile) => (
                                            <div key={tile.id} className="relative group/tooltip">
                                                <DiscoveryTile
                                                    icon={tile.icon}
                                                    title={
                                                        <span className="pr-1">
                                                            <span className="lg:hidden">Ask</span>
                                                            <span className="hidden lg:inline">Ask Anything</span>
                                                        </span>
                                                    }
                                                    description={tile.description} // Keep description for normal tile
                                                    bgColor={tile.bgColor}
                                                    borderColor={tile.borderColor}
                                                    textColor={tile.textColor}
                                                    iconBgColor={tile.iconBgColor}
                                                    onClick={() => setIsKanbanModalOpen(true)}
                                                    tabValue={tile.tabValue}
                                                    setActiveTabStart={setActiveTabStart}
                                                    isHome={isHome}
                                                    style={'icons'} // Normal style for Ask Anything
                                                />
                                                {/* No tooltip needed for normal style tile */}
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                // Original layout for !isHome
                                discoveryTilesData
                                    .map((tile) => {
                                        const isAskAnything = tile.id === 'conversation';
                                        const showIconOnly = isHome && !isAskAnything; // This logic is for !isHome path, but isHome is false here.
                                        // The original !isHome didn't use icons-only.
                                        return (
                                            <div key={tile.id} className="relative group/tooltip">
                                                <DiscoveryTile
                                                    icon={tile.icon}
                                                    title={tile.title}
                                                    description={tile.description}
                                                    bgColor={tile.bgColor}
                                                    borderColor={tile.borderColor}
                                                    textColor={tile.textColor}
                                                    iconBgColor={tile.iconBgColor}
                                                    onClick={() => setIsKanbanModalOpen(true)}
                                                    tabValue={tile.tabValue}
                                                    setActiveTabStart={setActiveTabStart}
                                                    style={'icons'} // All tiles use 'icons' style when !isHome
                                                    isHome={isHome}
                                                />
                                                {/* Tooltip logic was for isHome, not needed here for !isHome if all are 'icons' style */}
                                            </div>
                                        );
                                    })
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };

    const PaginationControls = ({ currentPage, totalPages, setCurrentPage }) => {
        return (
            <div className="flex items-center w-full justify-between gap-2">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-md ${currentPage === 1
                        ? 'bg-black/10 text-zinc-600 bg-zinc-900'
                        : 'bg-black/20 text-zinc-400 hover:bg-zinc-800'
                        }`}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                {/* Mobile: Page X of Y */}
                <div className="md:hidden text-zinc-400">
                    Page {currentPage} of {totalPages}
                </div>

                {/* Desktop: Numbered buttons */}
                <div className="hidden md:flex items-center gap-2">
                    {(() => {
                        let pages = [];
                        const maxVisible = 5;
                        let start = Math.max(1, Math.min(currentPage - Math.floor(maxVisible / 2), totalPages - maxVisible + 1));
                        let end = Math.min(start + maxVisible - 1, totalPages);

                        if (start > 1) {
                            pages.push(1);
                            if (start > 2) pages.push('...');
                        }

                        for (let i = start; i <= end; i++) {
                            pages.push(i);
                        }

                        if (end < totalPages) {
                            if (end < totalPages - 1) pages.push('...');
                            pages.push(totalPages);
                        }

                        return pages.map((page, index) => (
                            typeof page === 'number' ? (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-4 py-2 rounded-md ${currentPage === page
                                        ? 'bg-sky-500 text-white'
                                        : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                                        }`}
                                >
                                    {page}
                                </button>
                            ) : (
                                <span key={index} className="text-zinc-400">
                                    {page}
                                </span>
                            )
                        ));
                    })()}
                </div>

                <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-md ${currentPage === totalPages
                        ? 'bg-black/10 text-zinc-600 bg-zinc-900'
                        : 'bg-black/20 text-zinc-400 hover:bg-zinc-800'
                        }`}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        );
    };

    return (
        <div className={`${isHome ? 'h-auto mt-8 md:mt-0' : 'mx-auto h-[calc(100vh-200px)] mt-8 md:mt-0'}`}>

            {isKanbanModalOpen && selectedStoryData &&
                ReactDOM.createPortal(
                    <motion.div
                        className="fixed inset-0 w-full h-full bg-black/50 z-[9999] p-0 flex items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setIsKanbanModalOpen(false)}
                    >
                        <motion.div
                            className="bg-zinc-900 md:rounded-lg rounded-none w-full md:h-[calc(100%-48px)] h-[calc(100%-4px)] md:m-8 m-0 relative"
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            transition={{
                                type: "spring",
                                damping: 25,
                                stiffness: 300
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.button
                                type="button"
                                onClick={() => setIsKanbanModalOpen(false)}
                                className="absolute top-4 right-4 hidden md:block bg-zinc-800 rounded-full p-2 z-50 cursor-pointer text-zinc-400 hover:text-white"
                                whileHover={{ scale: 1.1, backgroundColor: "#3f3f46" }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </motion.button>

                            <KanbanBoard
                                storyVisualizationData={selectedStoryData.storyVisualizationData}
                                storyChaptersData={selectedStoryData.storyChapters}
                                formData={selectedStoryData.scenario}
                                scenario={selectedStoryData.scenario}
                                //intelligenceFoundation={selectedStoryData.intelligenceFoundation}
                                personaData={selectedStoryData.personaData}
                                generateStoryBite={generateStoryBite}
                                businessTransformationResult={selectedStoryData.businessTransformation}
                                businessCostOfInactionResult={selectedStoryData.businessCostOfInaction}
                                producedResult={selectedStoryData.producedResult}
                                businessAnalysisData={selectedStoryData.businessAnalysis}
                                salesAnalysisData={selectedStoryData.salesAnalysis}
                                storySpeakerSummaryResult={selectedStoryData.storySpeakerSummary}
                                intelligenceFoundation={selectedStoryData}
                                activeTabStart={activeTabStart}
                                researchData={selectedStoryData.researchData}
                                businessModelData={selectedStoryData.businessModelData}
                                researchDeepData={selectedStoryData.researchDeepData}
                                businessNewsData={selectedStoryData.businessNewsData}
                                industryTrendsData={selectedStoryData.industryTrendsData}
                                eli5DataBusiness={selectedStoryData.eli5DataBusiness}
                                eli5DataIndustry={selectedStoryData.eli5DataIndustry}
                                maxDeepData={selectedStoryData.maxDeepData}
                                eli5BusinessValue={selectedStoryData.eli5BusinessValue}
                                competitiveAnalysisResult={selectedStoryData.competitiveAnalysisResult}
                                caseStudiesData={selectedStoryData.caseStudiesData}
                                onRequestClose={() => setIsKanbanModalOpen(false)}
                            />
                        </motion.div>
                    </motion.div>,
                    document.body
                )
            }

            <Transition appear show={isDeleteModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={closeDeleteModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-zinc-900 p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-zinc-100"
                                    >
                                        Confirm Deletion
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-zinc-400">
                                            Are you sure you want to delete the path for "{storyToDelete?.scenario?.promptCustomerName || 'this path'}"? This action cannot be undone.
                                        </p>
                                    </div>

                                    <div className="mt-6 flex justify-end gap-3">
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-transparent bg-zinc-700 px-4 py-2 text-sm font-medium text-zinc-100 hover:bg-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 transition-colors"
                                            onClick={closeDeleteModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 transition-colors"
                                            onClick={handleConfirmDelete}
                                        >
                                            Delete Path
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {!isHome && (
                <div className="mb-6 flex w-full mx-auto items-center gap-4     rounded-lg">
                    <div className="relative flex-grow rounded-lg">

                        <input
                            type="text"
                            placeholder="Search paths..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="inline-flex w-full p-3 pl-4 bg-zinc-600/25 text-zinc-50 rounded-md border border-zinc-600 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 [&:-webkit-autofill]:bg-zinc-600/25 [&:-webkit-autofill]:!text-zinc-50 [&:-webkit-autofill]:shadow-[0_0_0_30px_rgb(82_82_91_/_0.25)_inset] [&:-webkit-autofill]:[filter:none]"
                        />

                        <svg
                            className="absolute right-4 top-3.5 h-5 w-5 text-zinc-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
            )}

            <div className={`space-y-4 ${isHome ? 'pt-2' : 'md:p-4 p-0 md:bg-black/5 rounded-lg'}`}>

                <div className="flex justify-between items-center px-2 md:px-0 pb-4">
                    <div className="flex items-center gap-2 ml-1.5">
                        {isHome && <img src="/util/app/b3-admin-path-navigate.svg" alt="Recent Paths Icon" className="w-10 h-10 text-zinc-100 opacity-75" />}
                        <h2 className="text-lg text-zinc-100 pl-1">Recent Paths</h2>
                    </div>
                    {isHome && (
                        <motion.div
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Link to="/app/storyboard?type=generate" className="px-4 py-3 text-base bg-black/10 hover:bg-black/30 text-white rounded-lg transition-colors">
                                <span className="text-sky-400 mr-2 text-xl font-bold">+</span>
                                Create New Path
                            </Link>
                        </motion.div>
                    )}
                    {!isHome && (
                        <div className="text-zinc-200 text-sm">
                            Showing {indexOfFirstStory + 1}-{Math.min(indexOfLastStory, filteredStories.length)} of {filteredStories.length} paths
                        </div>
                    )}
                </div>

                {loading && (
                    <div className={`grid gap-4 ${isHome ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                        <SkeletonTheme baseColor={baseColor} highlightColor={highlightColor}>
                            {/* Explicitly map to render skeletons with unique keys */}
                            {Array.from({ length: storiesPerPage }).map((_, index) => (
                                <Skeleton key={`skeleton-${index}`} className={`${isHome ? 'h-20' : 'h-[380px]'} rounded-lg`} />
                            ))}
                        </SkeletonTheme>
                    </div>
                )}

                {filteredStories.length > 0 && !loading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.25,
                            ease: "easeOut"
                        }}
                    >
                        <div className={`grid ${isHome ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'} gap-4`}>
                            {currentStories.map((story) => (
                                <ListViewItem
                                    key={story.id}
                                    story={story}
                                    onClick={handleStoryClick}
                                    onDeleteClick={openDeleteModal}
                                    isHome={isHome}
                                />
                            ))}
                        </div>
                    </motion.div>
                ) : (
                    <>
                        {!loading ?
                            <div className="flex flex-col items-center justify-center py-16 px-4 rounded-lg text-center">
                                <img
                                    src="/util/app/b3-admin-path-navigate.svg"
                                    alt="No paths"
                                    className="w-24 h-24 opacity-50 mb-6"
                                />
                                <h3 className="text-lg text-zinc-50 mb-2">No Paths Found</h3>
                                <p className="text-zinc-400 max-w-md mb-6">
                                    {searchQuery ?
                                        `We couldn't find any paths matching "${searchQuery}". Try adjusting your search criteria.` :
                                        "You don't have any Paths yet or none match your filters. Change your filter or create your first Path to get started."}
                                </p>
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Link
                                        to="/app/storyboard?type=blank"
                                        className="px-4 py-3 text-base bg-violet-950 hover:bg-violet-900 text-sky-400 rounded-lg transition-colors"
                                    >
                                        Create First Path
                                    </Link>
                                </motion.div>
                            </div>
                            :
                            null
                        }
                    </>
                )}

                {filteredStories.length > 0 && totalPages > 1 && !isHome && (
                    <div className="mt-8 px-0   flex justify-center items-center">
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            setCurrentPage={setCurrentPage}
                        />
                        <div className="mt-8 px-0 h-12  flex justify-center items-center">
                        </div>
                    </div>
                )}

                {isHome && stories.length > 0 && (
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={() => navigate('/app/paths')}
                            className="px-4 py-3 text-base bg-black/10 hover:bg-black/30 text-white rounded-lg transition-colors cursor-pointer flex items-center"
                        >
                            View All
                            <img className="h-4 w-4 inline-block ml-2" src="/util/app/b3-admin-arrow-right-sky-400.svg" alt="View all paths" />
                        </button>
                    </div>
                )}

            </div>

        </div>
    );
};

export default PathLibrary; 